import { forwardRef, useCallback, useId, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ChevronDown,
  Dumbbell,
  Flame,
  GripVertical,
  Loader2,
  Plus,
  TrendingDown,
  Trash2,
  Zap,
} from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { Accordion, AccordionContent, AccordionItem } from '../ui/accordion'
import { AnimatedToggleGroup, AnimatedToggleGroupItem } from '../ui/animated-toggle-group'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Switch } from '../ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'
import { useSimpleToast } from '../../hooks/useToast'
import { cn } from '../../lib/utils'
import { CountAndRestField } from './CountAndRestField'
import { DurationField } from './DurationField'
import { SliderField } from './SliderField'
import {
  emptyTemplateInput,
  formatSeconds,
  isTemplateDirty,
  isValid,
  templateDurationSeconds,
  templateToInput,
  templateToPayload,
  validateTemplate,
  type FieldErrors,
} from './custom-exercise-form'
import { exerciseTemplatesService } from '../../services/exercise-templates'
import {
  EXERCISE_TEMPLATE_KINDS,
  EXERCISE_TEMPLATE_TRANSITIONS,
  type ExerciseTemplateInput,
  type ExerciseTemplateKind,
  type ExerciseTemplateMode,
  type ExerciseTemplateTransition,
} from '../../types/exercise-template'

const MAX_COUNT = 30
const MAX_REPS_TARGET = 50

/**
 * What each kind of block looks like in the picker. The names run long in both
 * languages ("Riscaldamento", "A cedimento") and four of them will not fit
 * across a phone, so the icon carries the meaning and the word joins it once
 * there is room. The colours are what tell them apart at a glance.
 */
const KIND_ICONS: Record<ExerciseTemplateKind, { icon: typeof Dumbbell; color: string }> = {
  normal: { icon: Dumbbell, color: 'text-foreground' },
  warmup: { icon: Flame, color: 'text-amber-500' },
  dropset: { icon: TrendingDown, color: 'text-sky-500' },
  failure: { icon: Zap, color: 'text-red-500' },
}

export interface ExerciseTemplateEditorHandle {
  /**
   * Writes the open block if it has changes. The page's own Save calls this
   * first, so "save everything" means what it says. False when the block failed
   * validation — the page must not navigate away over it.
   */
  flush: () => Promise<boolean>
}

interface ExerciseTemplateEditorProps {
  /** The exercise these blocks belong to. It always exists by this point. */
  exerciseId: string
  templates: ExerciseTemplateInput[]
  onTemplatesChange: (templates: ExerciseTemplateInput[]) => void
}

/**
 * The blocks of a custom exercise, each one an ExerciseTemplate.
 *
 * Everything here is saved as it is decided rather than at the end: adding a
 * block creates it, dragging one writes the new order, and changing the pause
 * that follows a block writes that. What is *not* immediate is the body of a
 * block — it is a form, and a half-typed one should not be saved — so each has
 * a Save of its own, and closing one with unsaved changes asks first.
 *
 * Only one block is open at a time. They are a list of similar things and only
 * one is ever being written; leaving the others expanded would make the running
 * order impossible to see.
 */
export const ExerciseTemplateEditor = forwardRef<
  ExerciseTemplateEditorHandle,
  ExerciseTemplateEditorProps
>(function ExerciseTemplateEditor({ exerciseId, templates, onTemplatesChange }, ref) {
  const { t } = useTranslation()
  const { showError } = useSimpleToast()

  const [open, setOpen] = useState('')
  const [errors, setErrors] = useState<FieldErrors>({})
  const [busy, setBusy] = useState(false)
  // What each block looked like when it was last written, to tell a block that
  // has been edited from one that has merely been opened.
  const [saved, setSaved] = useState<Record<string, ExerciseTemplateInput>>(() =>
    Object.fromEntries(templates.map((template) => [template.key, template]))
  )
  const [pendingSwitch, setPendingSwitch] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<ExerciseTemplateInput | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  // Read through a ref so `flush` can stay stable while still seeing the block
  // as it is at the moment the page asks for it.
  const latest = useRef({ templates, saved, open })
  latest.current = { templates, saved, open }

  const isDirty = useCallback(
    (key: string) => {
      const { templates: current, saved: snapshots } = latest.current
      const template = current.find((item) => item.key === key)
      const snapshot = snapshots[key]
      return Boolean(template && snapshot && isTemplateDirty(template, snapshot))
    },
    []
  )

  function patchTemplate(key: string, patch: Partial<ExerciseTemplateInput>) {
    onTemplatesChange(
      templates.map((template) => (template.key === key ? { ...template, ...patch } : template))
    )
  }

  const saveTemplate = useCallback(
    async (key: string): Promise<boolean> => {
      const template = latest.current.templates.find((item) => item.key === key)
      if (!template) return true

      const found = validateTemplate(template)
      setErrors(found)
      if (!isValid(found)) return false

      setBusy(true)
      try {
        const stored = await exerciseTemplatesService.update(
          exerciseId,
          template.id,
          templateToPayload(template)
        )
        // Keep the local key so the accordion and the drag list do not lose
        // their place over a save.
        const refreshed = { ...templateToInput(stored), key }
        onTemplatesChange(
          latest.current.templates.map((item) => (item.key === key ? refreshed : item))
        )
        setSaved((prev) => ({ ...prev, [key]: refreshed }))
        return true
      } catch {
        showError(t('trainingDiary.customExercise.blocks.saveError', 'Could not save this block'))
        return false
      } finally {
        setBusy(false)
      }
    },
    [exerciseId, onTemplatesChange, showError, t]
  )

  useImperativeHandle(
    ref,
    () => ({
      flush: async () => {
        const key = latest.current.open
        if (!key || !isDirty(key)) return true
        const ok = await saveTemplate(key)
        if (ok) setOpen('')
        return ok
      },
    }),
    [isDirty, saveTemplate]
  )

  async function addBlock() {
    setBusy(true)
    try {
      const draft = emptyTemplateInput()
      draft.title = t('trainingDiary.customExercise.blocks.defaultTitle', 'Block {{n}}', {
        n: templates.length + 1,
      })
      const stored = await exerciseTemplatesService.create(exerciseId, templateToPayload(draft))
      const created = { ...templateToInput(stored), key: draft.key }
      onTemplatesChange([...templates, created])
      setSaved((prev) => ({ ...prev, [created.key]: created }))
      setOpen(created.key)
      setErrors({})
    } catch {
      showError(t('trainingDiary.customExercise.blocks.addError', 'Could not add a block'))
    } finally {
      setBusy(false)
    }
  }

  async function deleteBlock(template: ExerciseTemplateInput) {
    setBusy(true)
    try {
      await exerciseTemplatesService.destroy(exerciseId, template.id)
      onTemplatesChange(templates.filter((item) => item.key !== template.key))
      setSaved((prev) => {
        const next = { ...prev }
        delete next[template.key]
        return next
      })
      if (open === template.key) setOpen('')
    } catch {
      showError(t('trainingDiary.customExercise.blocks.deleteError', 'Could not delete this block'))
    } finally {
      setBusy(false)
      setPendingDelete(null)
    }
  }

  async function handleDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return
    const from = templates.findIndex((template) => template.key === active.id)
    const to = templates.findIndex((template) => template.key === over.id)
    if (from === -1 || to === -1) return

    const reordered = arrayMove(templates, from, to)
    onTemplatesChange(reordered)

    try {
      await exerciseTemplatesService.reorder(
        exerciseId,
        reordered.map((template) => template.id)
      )
    } catch {
      // Put it back: a list that stays where it was dropped while the server
      // holds the old order is the one outcome worth avoiding.
      onTemplatesChange(templates)
      showError(t('trainingDiary.customExercise.blocks.reorderError', 'Could not save the new order'))
    }
  }

  /** The pause is one field, saved on the spot — there is nothing to draft. */
  async function saveTransition(
    template: ExerciseTemplateInput,
    kind: ExerciseTemplateTransition,
    seconds: number | null
  ) {
    const previous = template
    patchTemplate(template.key, { transition_kind: kind, transition_seconds: seconds })

    try {
      const stored = await exerciseTemplatesService.update(exerciseId, template.id, {
        transition_kind: kind,
        ...(kind === 'timed' ? { transition_seconds: seconds ?? 0 } : {}),
      })
      const refreshed = { ...templateToInput(stored), key: template.key }
      // Only the pause was sent, so only the pause is taken from the answer —
      // the rest of the block may be mid-edit.
      patchTemplate(template.key, {
        transition_kind: refreshed.transition_kind,
        transition_seconds: refreshed.transition_seconds,
      })
      setSaved((prev) => ({
        ...prev,
        [template.key]: {
          ...(prev[template.key] ?? refreshed),
          transition_kind: refreshed.transition_kind,
          transition_seconds: refreshed.transition_seconds,
        },
      }))
    } catch {
      patchTemplate(template.key, {
        transition_kind: previous.transition_kind,
        transition_seconds: previous.transition_seconds,
      })
      showError(t('trainingDiary.customExercise.blocks.saveError', 'Could not save this block'))
    }
  }

  function handleAccordionChange(next: string) {
    if (open && open !== next && isDirty(open)) {
      setPendingSwitch(next)
      return
    }
    setErrors({})
    setOpen(next)
  }

  async function confirmSwitchSaving() {
    const target = pendingSwitch
    setPendingSwitch(null)
    if (await saveTemplate(open)) setOpen(target ?? '')
  }

  function confirmSwitchDiscarding() {
    const target = pendingSwitch
    const snapshot = saved[open]
    setPendingSwitch(null)
    if (snapshot) {
      onTemplatesChange(templates.map((item) => (item.key === open ? snapshot : item)))
    }
    setErrors({})
    setOpen(target ?? '')
  }

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <h2 className="text-sm font-medium">
            {t('trainingDiary.customExercise.blocks.title', 'Timer protocol')}
          </h2>
          <p className="text-xs text-muted-foreground">
            {templates.length > 1
              ? t('trainingDiary.customExercise.blocks.reorderHint', 'Drag a block to reorder it.')
              : t(
                  'trainingDiary.customExercise.blocks.hint',
                  'Optional. Add blocks to make the workout runnable with the timer.'
                )}
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" disabled={busy} onClick={addBlock}>
          <Plus className="size-3.5 mr-1" />
          {t('trainingDiary.customExercise.blocks.add', 'Block')}
        </Button>
      </div>

      {templates.length === 0 ? (
        <p className="rounded-lg border border-dashed p-4 text-xs text-muted-foreground text-center">
          {t(
            'trainingDiary.customExercise.blocks.empty',
            'No blocks yet — the workout will be saved without a timer protocol.'
          )}
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={templates.map((template) => template.key)}
            strategy={verticalListSortingStrategy}
          >
            <Accordion
              type="single"
              collapsible
              value={open}
              onValueChange={handleAccordionChange}
              className="flex flex-col gap-2"
            >
              {templates.map((template, index) => (
                <SortableBlock
                  key={template.key}
                  template={template}
                  index={index}
                  isLast={index === templates.length - 1}
                  isOpen={open === template.key}
                  busy={busy}
                  errors={errors}
                  onChange={(patch) => patchTemplate(template.key, patch)}
                  onSave={() => saveTemplate(template.key).then((ok) => ok && setOpen(''))}
                  onRemove={() => setPendingDelete(template)}
                  onTransitionChange={(kind, seconds) => saveTransition(template, kind, seconds)}
                />
              ))}
            </Accordion>
          </SortableContext>
        </DndContext>
      )}

      <AlertDialog open={pendingSwitch != null} onOpenChange={(o) => !o && setPendingSwitch(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('trainingDiary.customExercise.blocks.unsavedTitle', 'Save this block?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'trainingDiary.customExercise.blocks.unsavedBody',
                'This block has changes that have not been saved. Closing it without saving loses them.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={confirmSwitchDiscarding}>
              {t('trainingDiary.customExercise.blocks.discard', 'Close without saving')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmSwitchSaving}>
              {t('common.save', 'Save')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pendingDelete != null} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('trainingDiary.customExercise.blocks.deleteTitle', 'Delete this block?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'trainingDiary.customExercise.blocks.deleteBody',
                'It is removed from the exercise straight away.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => pendingDelete && deleteBlock(pendingDelete)}>
              {t('common.delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
})

interface SortableBlockProps {
  template: ExerciseTemplateInput
  index: number
  isLast: boolean
  isOpen: boolean
  busy: boolean
  errors: FieldErrors
  onChange: (patch: Partial<ExerciseTemplateInput>) => void
  onSave: () => void
  onRemove: () => void
  onTransitionChange: (kind: ExerciseTemplateTransition, seconds: number | null) => void
}

function SortableBlock({
  template,
  index,
  isLast,
  isOpen,
  busy,
  errors,
  onChange,
  onSave,
  onRemove,
  onTransitionChange,
}: SortableBlockProps) {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: template.key,
  })

  const errorFor = (field: string) => (isOpen ? errors[field] : undefined)

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn('flex flex-col gap-2', isDragging && 'opacity-50 z-10')}
    >
      <AccordionItem value={template.key} className="rounded-lg border bg-card">
        <div className="flex items-center gap-1 pl-1 pr-1.5">
          <button
            type="button"
            className="cursor-grab touch-none p-1.5 text-muted-foreground active:cursor-grabbing"
            aria-label={t('trainingDiary.customExercise.blocks.reorder', 'Reorder block {{n}}', {
              n: index + 1,
            })}
            {...attributes}
            {...listeners}
          >
            <GripVertical className="size-4" aria-hidden />
          </button>

          <AccordionPrimitive.Header className="flex flex-1 min-w-0">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center gap-2 min-w-0 py-2.5 text-left [&[data-state=open]>svg]:rotate-180">
              <span className="flex flex-col min-w-0 flex-1">
                <span className="text-sm font-medium truncate">
                  {template.title.trim() ||
                    t('trainingDiary.customExercise.blocks.untitled', 'Untitled block')}
                </span>
                <span className="text-xs text-muted-foreground truncate">
                  <BlockSummary template={template} />
                </span>
              </span>
              <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform duration-200" />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 shrink-0 text-destructive"
            aria-label={t('trainingDiary.customExercise.blocks.remove', 'Remove block')}
            onClick={onRemove}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>

        <AccordionContent className="px-3 pb-3 pt-0 flex flex-col gap-4">
          <BlockFields template={template} errorFor={errorFor} onChange={onChange} />

          <Button type="button" disabled={busy} onClick={onSave}>
            {busy && <Loader2 className="size-4 mr-1 animate-spin" />}
            {t('trainingDiary.customExercise.blocks.save', 'Save block')}
          </Button>
        </AccordionContent>
      </AccordionItem>

      {/* The pause belongs to the block above it, so it travels with it when
          that block is dragged — and stays in view whether the block is open
          or not, because it is what separates one from the next. */}
      {!isLast && <PauseStrip template={template} onChange={onTransitionChange} />}
    </div>
  )
}

function BlockFields({
  template,
  errorFor,
  onChange,
}: {
  template: ExerciseTemplateInput
  errorFor: (field: string) => string | undefined
  onChange: (patch: Partial<ExerciseTemplateInput>) => void
}) {
  const { t } = useTranslation()

  return (
    <>
      <Field
        label={t('trainingDiary.customExercise.blocks.blockTitle', 'Block name')}
        error={errorFor('title')}
      >
        {(id) => (
          <Input
            id={id}
            value={template.title}
            onChange={(e) => onChange({ title: e.target.value })}
            placeholder={t('trainingDiary.customExercise.blocks.blockTitlePlaceholder', 'e.g. Work set')}
          />
        )}
      </Field>

      {/* The work mode leads, because it decides what the next field even is. */}
      <Tabs
        value={template.mode}
        onValueChange={(value) => {
          const mode = value as ExerciseTemplateMode
          // The control the new mode reveals must hold the value it is showing:
          // a slider sitting at 1 over a null would read as set and refuse to
          // save.
          onChange(
            mode === 'manual'
              ? { mode, reps_target: template.reps_target ?? 1 }
              : { mode, work_seconds: template.work_seconds ?? 10 }
          )
        }}
      >
        <TabsList className="w-full">
          <TabsTrigger value="timed" className="flex-1">
            {t('trainingDiary.customExercise.blocks.modeTimed', 'Timed')}
          </TabsTrigger>
          <TabsTrigger value="manual" className="flex-1">
            {t('trainingDiary.customExercise.blocks.modeManual', 'Reps')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="timed">
          <Field
            label={t('trainingDiary.customExercise.blocks.workSeconds', 'Work per rep')}
            hint={t('trainingDiary.customExercise.blocks.workSecondsHint', 'The timer counts this down on its own.')}
            error={errorFor('work_seconds')}
          >
            {(id) => (
              <DurationField
                id={id}
                label={t('trainingDiary.customExercise.blocks.workSeconds', 'Work per rep')}
                value={template.work_seconds}
                min={1}
                invalid={Boolean(errorFor('work_seconds'))}
                onChange={(work_seconds) => onChange({ work_seconds })}
              />
            )}
          </Field>
        </TabsContent>

        <TabsContent value="manual">
          <SliderField
            label={t('trainingDiary.customExercise.blocks.repsTarget', 'Target reps')}
            value={template.reps_target ?? 1}
            onChange={(reps_target) => onChange({ reps_target })}
            min={1}
            max={MAX_REPS_TARGET}
          />
        </TabsContent>
      </Tabs>

      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">{t('trainingDiary.customExercise.blocks.kind', 'Type')}</Label>
        <AnimatedToggleGroup
          value={template.kind}
          onValueChange={(kind) => onChange({ kind: kind as ExerciseTemplateKind })}
          aria-label={t('trainingDiary.customExercise.blocks.kind', 'Type')}
        >
          {EXERCISE_TEMPLATE_KINDS.map((kind) => {
            const { icon: Icon, color } = KIND_ICONS[kind]
            const label = t(`trainingDiary.customExercise.blocks.kinds.${kind}`, kind)

            return (
              <AnimatedToggleGroupItem
                key={kind}
                value={kind}
                aria-label={label}
                className="flex items-center justify-center gap-1.5"
              >
                <Icon className={cn('size-4 shrink-0', color)} aria-hidden />
                <span className="hidden truncate sm:inline">{label}</span>
              </AnimatedToggleGroupItem>
            )
          })}
        </AnimatedToggleGroup>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <CountAndRestField
          countLabel={t('trainingDiary.customExercise.blocks.sets', 'Sets')}
          restLabel={t('trainingDiary.customExercise.blocks.setRestSeconds', 'Rest between sets')}
          count={template.sets}
          restSeconds={template.set_rest_seconds}
          max={MAX_COUNT}
          invalid={Boolean(errorFor('sets'))}
          onChange={({ count, restSeconds }) =>
            onChange({ sets: count, set_rest_seconds: restSeconds })
          }
        />
        <CountAndRestField
          countLabel={t('trainingDiary.customExercise.blocks.repsPerSet', 'Reps per set')}
          restLabel={t('trainingDiary.customExercise.blocks.restSeconds', 'Rest between reps')}
          count={template.reps_per_set}
          restSeconds={template.rest_seconds}
          max={MAX_COUNT}
          invalid={Boolean(errorFor('reps_per_set'))}
          onChange={({ count, restSeconds }) =>
            onChange({ reps_per_set: count, rest_seconds: restSeconds })
          }
        />
      </div>

      <OptionalFields template={template} errorFor={errorFor} onChange={onChange} />

      <BlockTotal template={template} />
    </>
  )
}

function BlockTotal({ template }: { template: ExerciseTemplateInput }) {
  const { t } = useTranslation()
  const duration = templateDurationSeconds(template)

  return (
    <p className="text-xs text-muted-foreground border-t pt-2">
      {t('trainingDiary.customExercise.blocks.summary', {
        sets: template.sets ?? 0,
        reps: template.reps_per_set ?? 0,
        defaultValue: '{{sets}} sets × {{reps}} reps',
      })}
      {duration != null && ` · ${formatSeconds(duration)}`}
    </p>
  )
}

/** The collapsed row's second line: manual or timed, and how much of it. */
function BlockSummary({ template }: { template: ExerciseTemplateInput }) {
  const { t } = useTranslation()

  if (template.mode === 'manual') {
    return (
      <>
        {t('trainingDiary.customExercise.blocks.summaryManual', {
          sets: template.sets ?? 0,
          reps: template.reps_per_set ?? 0,
          target: template.reps_target ?? 0,
          defaultValue: '{{sets}}×{{reps}} · {{target}} reps, manual',
        })}
      </>
    )
  }

  return (
    <>
      {t('trainingDiary.customExercise.blocks.summaryTimed', {
        sets: template.sets ?? 0,
        reps: template.reps_per_set ?? 0,
        seconds: template.work_seconds ?? 0,
        defaultValue: '{{sets}}×{{reps}} · {{seconds}}s timed',
      })}
    </>
  )
}

/**
 * What happens between this block and the next, sitting where it happens.
 *
 * Saved the moment it changes: it is one choice with nothing to draft, unlike
 * the block body above it. Picking a timed pause opens the wheels straight
 * away and only writes once they are confirmed, so a pause can never be stored
 * without a length.
 */
function PauseStrip({
  template,
  onChange,
}: {
  template: ExerciseTemplateInput
  onChange: (kind: ExerciseTemplateTransition, seconds: number | null) => void
}) {
  const { t } = useTranslation()
  const [sheetOpen, setSheetOpen] = useState(false)

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-dashed bg-muted/30 px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
          {t('trainingDiary.customExercise.blocks.transition', 'Pause before the next block')}
        </span>
        {template.transition_kind === 'timed' && (
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className="text-xs font-medium underline underline-offset-2"
          >
            {formatSeconds(template.transition_seconds ?? 0)}
          </button>
        )}
      </div>

      <AnimatedToggleGroup
        value={template.transition_kind}
        onValueChange={(kind) => {
          if (kind === 'timed') return setSheetOpen(true)
          onChange(kind as ExerciseTemplateTransition, null)
        }}
        aria-label={t('trainingDiary.customExercise.blocks.transition', 'Pause before the next block')}
      >
        {EXERCISE_TEMPLATE_TRANSITIONS.map((kind) => (
          <AnimatedToggleGroupItem key={kind} value={kind}>
            {t(`trainingDiary.customExercise.blocks.transitions.${kind}`, kind)}
          </AnimatedToggleGroupItem>
        ))}
      </AnimatedToggleGroup>

      <DurationField
        hideTrigger
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        label={t('trainingDiary.customExercise.blocks.transitionSeconds', 'Pause')}
        value={template.transition_seconds}
        min={1}
        onChange={(seconds) => onChange('timed', seconds)}
      />
    </div>
  )
}

function OptionalFields({
  template,
  errorFor,
  onChange,
}: {
  template: ExerciseTemplateInput
  errorFor: (field: string) => string | undefined
  onChange: (patch: Partial<ExerciseTemplateInput>) => void
}) {
  const { t } = useTranslation()
  const [showOptional, setShowOptional] = useState(false)

  return (
    <>
      <button
        type="button"
        onClick={() => setShowOptional((v) => !v)}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors self-start"
      >
        <ChevronDown className={cn('size-3.5 transition-transform', showOptional && 'rotate-180')} />
        {t('trainingDiary.customExercise.blocks.optional', 'Load, RPE and sides')}
      </button>

      {showOptional && (
        <div className="flex flex-col gap-4 rounded-md bg-muted/40 p-3">
          <SliderField
            label={t('trainingDiary.customExercise.blocks.weightPercent', 'Load')}
            value={template.weight_percent}
            onChange={(weight_percent) => onChange({ weight_percent })}
            min={0}
            max={200}
            step={5}
            clearAt={0}
            format={(value) => `${value}%`}
          />

          <SliderField
            label={t('trainingDiary.customExercise.blocks.rpe', 'RPE')}
            value={template.rpe}
            onChange={(rpe) => onChange({ rpe })}
            min={0}
            max={10}
            clearAt={0}
          />

          <div className="flex items-center justify-between gap-3">
            <Label htmlFor={`alternate-${template.key}`} className="text-xs font-normal">
              {t('trainingDiary.customExercise.blocks.alternateSide', 'Alternate sides')}
            </Label>
            <Switch
              id={`alternate-${template.key}`}
              checked={template.alternate_side}
              onCheckedChange={(alternate_side) =>
                // A side-change delay is only valid on a block that alternates;
                // dropping it here keeps the payload acceptable to the server.
                onChange({
                  alternate_side,
                  side_change_seconds: alternate_side ? template.side_change_seconds : null,
                })
              }
            />
          </div>

          {template.alternate_side && (
            <Field
              label={t('trainingDiary.customExercise.blocks.sideChangeSeconds', 'Side change')}
              error={errorFor('side_change_seconds')}
            >
              {(id) => (
                <DurationField
                  id={id}
                  label={t('trainingDiary.customExercise.blocks.sideChangeSeconds', 'Side change')}
                  value={template.side_change_seconds}
                  min={1}
                  invalid={Boolean(errorFor('side_change_seconds'))}
                  onChange={(side_change_seconds) => onChange({ side_change_seconds })}
                />
              )}
            </Field>
          )}
        </div>
      )}
    </>
  )
}

/**
 * Labelled form row. The control comes in as a render prop so it can take the
 * generated id: every control here needs its label actually tied to it.
 */
function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string
  hint?: string
  error?: string
  children: (id: string) => React.ReactNode
}) {
  const { t } = useTranslation()
  const id = useId()

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className="text-xs">{label}</Label>
      {children(id)}
      {hint && !error && <p className="text-[11px] text-muted-foreground">{hint}</p>}
      {error && (
        <p className="text-[11px] text-destructive">
          {t(`trainingDiary.customExercise.errors.${error}`, error)}
        </p>
      )}
    </div>
  )
}
